// ============================================================================
// Shared Hook: useDropdownOptions
// ============================================================================
// Extracts dropdown options (gridSizes, depths, labPackages, micros) from deals.
// Used by both JobDetailModal and MissingInvalidData/JobImportForm.

import { useMemo } from "react";
import type { Deal, Job } from "../../../models";

export interface DropdownOptions {
	/** Available grid sizes (densities) from deals */
	gridSizes: number[];
	/** Available depths from deals */
	depths: number[];
	/** Available lab packages from deals */
	labPackages: string[];
	/** Available micro frequencies from deals */
	micros: number[];
}

export interface UseDropdownOptionsParams {
	/** All available deals to extract options from */
	allDeals: Deal[];
	/** Current job (to include its current values in options) */
	job?: Job | null;
}

/**
 * Hook to compute dropdown options from deals
 *
 * Extracts unique values from all deals and includes the current job's values
 * to ensure they're always selectable even if not in any deal.
 */
export function useDropdownOptions({ allDeals, job }: UseDropdownOptionsParams): DropdownOptions {
	const gridSizes = useMemo(() => {
		return [
			...new Set([...allDeals.flatMap((deal) => deal.density), ...(job?.importDensity ? [job.importDensity] : [])]),
		].sort((a, b) => a - b);
	}, [allDeals, job?.importDensity]);

	const depths = useMemo(() => {
		return [
			...new Set([...allDeals.flatMap((deal) => deal.depth), ...(job?.importDepth ? [job.importDepth] : [])]),
		].sort((a, b) => a - b);
	}, [allDeals, job?.importDepth]);

	const labPackages = useMemo(() => {
		return [
			...new Set([
				...allDeals.flatMap((deal) => (deal.testPackage ? deal.testPackage : [])),
				...(job?.externalTestPackageId ? [job.externalTestPackageId] : []),
				"No test package specified",
			]),
		];
	}, [allDeals, job?.externalTestPackageId]);

	const micros = useMemo(() => {
		return [
			...new Set([
				...allDeals.flatMap((deal) => deal.frequencyOfMicros ?? 0),
				...(job?.externalFrequencyOfMicros ? [job.externalFrequencyOfMicros] : []),
			]),
		].sort((a, b) => a - b);
	}, [allDeals, job?.externalFrequencyOfMicros]);

	return { gridSizes, depths, labPackages, micros };
}
